import { CHART_MODEBAR_BUTTONS_TO_REMOVE } from "../../../constants";
import Plotly from "plotly.js/dist/plotly";
import { enqueueSnackbar } from "notistack";

// $text (styles/_tokens.scss): the exported title reads as a title, not as
// the grey axis labels.
const EXPORT_TITLE_COLOR = "#575858";

// A trait as the chart titles write it: its name, then its unit. An AUC trait
// is named "<technical name>|<dates>" (BreederReport): print its base trait
// with the dates, as PropertyName does on screen.
export const traitLabel = (trait) => {
  const base = trait.baseTrait ?? trait;
  const dates = trait.baseTrait ? ` [${trait.name.split("|")[1]}]` : "";
  return `${base.name}${dates}${base.unit ? ` (${base.unit})` : ""}`;
};

/* Plotly's own PNG export only draws the plot: the chart title is HTML, so it
 * is missing, and a legend wider than its margin is cut at the plot width.
 * This draws the same figure for a slide: the title on top, the legend under
 * the plot in two fixed half-width columns (sized from each name, they
 * overlapped in the image), at least 900x550. `layout` adds a chart's own
 * changes, from the figure's layout.
 *
 * Plotly caps a legend under the plot at half the image height and cuts the
 * rest (legend/draw.js): the image grows to twice the legend. A row is 19px
 * up to a 12px font, plus 10px of padding (legend/draw.js). Past 60 entries
 * the plot would stretch with it, for colours that repeat anyway: the legend
 * is dropped and a warning says so at the click.
 */
const LEGEND_ROW_HEIGHT = 19;
const LEGEND_PADDING = 10;
const MAX_LEGEND_ENTRIES = 60;

const downloadSlideImage = (graphDiv, { title, filename, layout: extra }) => {
  const { layout } = graphDiv;
  const legendEntries = graphDiv._fullLayout.showlegend
    ? graphDiv._fullData.filter((trace) => trace.showlegend).length
    : 0;
  const hideLegend = legendEntries > MAX_LEGEND_ENTRIES;
  const legendHeight = hideLegend
    ? 0
    : Math.ceil(legendEntries / 2) * LEGEND_ROW_HEIGHT + LEGEND_PADDING;
  if (hideLegend)
    enqueueSnackbar(
      `Too many series (${legendEntries}) to show a legend: the image is exported without it. Choose fewer series to get one.`,
      { variant: "warning" }
    );
  Plotly.downloadImage(
    {
      data: graphDiv.data,
      layout: {
        ...layout,
        title: {
          text: title,
          x: 0,
          xanchor: "left",
          font: { size: 15, color: EXPORT_TITLE_COLOR },
        },
        margin: { ...layout.margin, t: 40 },
        legend: {
          ...layout.legend,
          orientation: "h",
          entrywidth: 0.5,
          entrywidthmode: "fraction",
          x: 0,
          xanchor: "left",
          y: -0.12,
          yanchor: "top",
        },
        ...extra?.(layout),
        ...(hideLegend && { showlegend: false }),
      },
    },
    {
      format: "png",
      filename,
      width: Math.max(graphDiv._fullLayout.width, 900),
      height: Math.max(graphDiv._fullLayout.height, 550, 2 * legendHeight),
    }
  );
};

// Plotly config entries replacing the camera button with the slide export.
// Spread after COMMON_PLOT_CONFIG: it overrides its removed buttons.
export const slideExportConfig = ({ title, filename, layout }) => ({
  modeBarButtonsToRemove: [...CHART_MODEBAR_BUTTONS_TO_REMOVE, "toImage"],
  modeBarButtonsToAdd: [
    {
      // The modebar only redraws its buttons when a name changes, keeping the
      // first click and its title and filename: name the button after them.
      name: `slide-export:${title}:${filename}`,
      title: "Download plot as a png",
      icon: Plotly.Icons.camera,
      click: (graphDiv) =>
        downloadSlideImage(graphDiv, { title, filename, layout }),
    },
  ],
});
